// The AppBar every Flutter device screen shares: avatar, device name, a
// "Location > Room" subtitle, a pencil that renames the device, and the ⋮ menu
// from allDeviceFunctions.dart (DeviceMenuButton).
//
// The Dart menu publishes `getallwifi` before opening any WiFi entry so the
// device's list is fresh by the time the sheet appears; the same publish happens
// here. WiFi entries reuse DeviceWifiPanel, which already speaks that protocol.

import React, { useState } from 'react';
import {
  IconButton, Menu, MenuItem, ListItemIcon, ListItemText, Dialog, DialogTitle,
  DialogContent, DialogActions, TextField, Button, Typography, Box, useTheme,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import MoreVertIcon from '@mui/icons-material/MoreVert';
import InfoIcon from '@mui/icons-material/Info';
import SignalWifi4BarIcon from '@mui/icons-material/SignalWifi4Bar';
import SignalWifiBadSharpIcon from '@mui/icons-material/SignalWifiBadSharp';
import SystemUpdateAltIcon from '@mui/icons-material/SystemUpdateAlt';
import UpdateCodeDialog from '../../../../shared/UpdateCodeDialog';
import { ProfileTitleBar } from '../../../components/MobileAppBar';
import { useMessage } from '../../../components/MessageDialog';
import { useApp } from '../../../../../core/state/AppContext';
import { renameDeviceInstalled, getDeviceDetail } from '../../../../../core/api/api';
import { publishCommand } from '../../../../../core/utils/deviceController';
import { deviceLabel } from '../../../../../core/devices/deviceMeta';
import { CMD_GET_ALL_WIFI, buildAddWifiMqtt } from '../../../../../core/devices/commands';
import WifiPickerDialog from '../../../components/WifiPickerDialog';
import WifiRemoveDialog from '../../../components/WifiRemoveDialog';

export default function DeviceAppBar({
  device, extraItems = [], onRenamed, subtitle: subtitleOverride,
}) {
  const theme = useTheme();
  const c = theme.sli;
  const { userId, canUpdateFirmware } = useApp();
  const { show } = useMessage();

  const [anchor, setAnchor] = useState(null);
  const [renaming, setRenaming] = useState(false);
  const [newName, setNewName] = useState('');
  const [infoOpen, setInfoOpen] = useState(false);
  const [updateOpen, setUpdateOpen] = useState(false);
  const [wifiOpen, setWifiOpen] = useState(false);
  const [removeOpen, setRemoveOpen] = useState(false);
  // undefined = still resolving, null = could not be resolved.
  const [wifiLocationId, setWifiLocationId] = useState(undefined);

  const uno = device?.UniqueNumber;
  const title = device?.DeviceName || `Unnamed (${device?.DeviceTypeName ?? ''})`;
  // Most Flutter device screens show "Location > Room"; the phase meters show the
  // location alone, so they pass their own subtitle.
  const subtitle = subtitleOverride
    ?? `${device?.LocationName ?? ''} > ${device?.RoomName ?? ''}`;

  const submitRename = async () => {
    const name = newName.trim();
    if (!name) { show('Invalid Name', 'Device name cannot be empty.'); return; }
    if (name.length > 50) {
      show('Invalid Name', 'Device name cannot exceed 50 characters.');
      return;
    }
    try {
      await renameDeviceInstalled(uno, name);
      setRenaming(false);
      show('Name Updated', `Device name updated successfully to "${name}".`);
      if (onRenamed) onRenamed(name);
    } catch (e) {
      show('Update Failed',
        'An error occurred while updating the device name. Please check your network and try again.');
    }
  };

  const wifiThen = (fn) => () => {
    setAnchor(null);
    // Flutter refreshes the device's WiFi list before opening a WiFi entry.
    publishCommand(uno, CMD_GET_ALL_WIFI, userId);
    fn();
  };

  // The device's location, needed to offer the networks already in use there.
  //
  // ⚠️ NOT taken from the device row: the list endpoints return LocationName but
  // no LocationID. GetDeviceDetailbyUniqueNumber has it.
  const openAddWifi = async () => {
    setWifiOpen(true);
    setWifiLocationId(undefined);
    const res = await getDeviceDetail(uno).catch(() => null);
    const row = Array.isArray(res?.data) ? res.data[0] : null;
    setWifiLocationId(row ? (row.LocationID ?? row.LocationId ?? null) : null);
  };

  // ADDWIFI / REMOVEWIFI are the one pair the firmware does NOT upper-case,
  // because an SSID and a password are case-sensitive. buildAddWifiMqtt is the
  // only place that literal is written.
  const sendAddWifi = ({ ssid, pass }) => {
    publishCommand(uno, buildAddWifiMqtt(ssid, pass), userId);
    setWifiOpen(false);
    show('Wi-Fi Sent', `"${ssid}" was sent to this device. It may drop offline for `
      + 'a moment while it reconnects.');
  };

  const items = [
    {
      icon: <InfoIcon fontSize="small" />,
      title: 'Device Details',
      onClick: () => { setAnchor(null); setInfoOpen(true); },
    },
    {
      icon: <SignalWifi4BarIcon fontSize="small" />,
      title: 'Add WiFi',
      onClick: wifiThen(openAddWifi),
    },
    {
      icon: <SignalWifiBadSharpIcon fontSize="small" />,
      title: 'Remove WiFi',
      onClick: wifiThen(() => setRemoveOpen(true)),
    },
    // Firmware push for this one device — owner / super admin only.
    ...(canUpdateFirmware ? [{
      icon: <SystemUpdateAltIcon fontSize="small" />,
      title: 'Update Code',
      onClick: () => { setAnchor(null); setUpdateOpen(true); },
    }] : []),
    ...extraItems,
  ];

  return (
    <>
      <ProfileTitleBar
        title={title}
        subtitle={subtitle}
        actions={(
          <>
            <IconButton
              onClick={() => { setNewName(device?.DeviceName || ''); setRenaming(true); }}
              sx={{ color: c.text }}
            >
              <EditIcon />
            </IconButton>
            <IconButton onClick={(e) => setAnchor(e.currentTarget)} sx={{ color: c.text }}>
              <MoreVertIcon />
            </IconButton>
          </>
        )}
      />

      <Menu
        anchorEl={anchor}
        open={!!anchor}
        onClose={() => setAnchor(null)}
        PaperProps={{ sx: { bgcolor: c.button, borderRadius: '16px' } }}
      >
        {items.map((it) => (
          <MenuItem key={it.title} onClick={it.onClick}>
            <ListItemIcon sx={{ color: c.text, minWidth: 32 }}>{it.icon}</ListItemIcon>
            <ListItemText
              primary={it.title}
              primaryTypographyProps={{ sx: { color: c.text, fontWeight: 700 } }}
            />
          </MenuItem>
        ))}
      </Menu>

      {/* allDeviceFunctions.dart promptForDeviceName() */}
      <Dialog open={renaming} onClose={() => setRenaming(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '20px', width: 'min(80vw, 400px)' } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700, textAlign: 'center', fontSize: 20 }}>
          Enter New Device Name
        </DialogTitle>
        <DialogContent>
          <TextField
            autoFocus fullWidth placeholder="Type here..."
            value={newName} onChange={(e) => setNewName(e.target.value)}
            sx={{ mt: 1, '& .MuiInputBase-input': { color: c.text } }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRenaming(false)} sx={{ color: c.text }}>Cancel</Button>
          <Button variant="contained" onClick={submitRename}
            sx={{ bgcolor: c.button, color: '#fff', borderRadius: '12px' }}>
            Submit
          </Button>
        </DialogActions>
      </Dialog>

      {/* devices/infoScreen.dart, condensed to a sheet. */}
      <Dialog open={infoOpen} onClose={() => setInfoOpen(false)}
        PaperProps={{ sx: { bgcolor: c.back, borderRadius: '16px', minWidth: 300 } }}>
        <DialogTitle sx={{ color: c.text, fontWeight: 700 }}>Device Details</DialogTitle>
        <DialogContent>
          {[
            ['Name', device?.DeviceName],
            ['Type', deviceLabel(device?.DeviceID)],
            ['Device ID', device?.DeviceID],
            ['Unique Number', uno],
            ['Location', device?.LocationName],
            ['Room', device?.RoomName],
          ].map(([k, v]) => (
            <Box key={k} sx={{ display: 'flex', justifyContent: 'space-between', py: 0.5, gap: 3 }}>
              <Typography sx={{ color: `${c.text}B3` }}>{k}</Typography>
              <Typography sx={{ color: c.text, fontWeight: 600 }}>{v ?? '—'}</Typography>
            </Box>
          ))}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setInfoOpen(false)} sx={{ color: c.text }}>Close</Button>
        </DialogActions>
      </Dialog>

      <UpdateCodeDialog
        open={updateOpen}
        scope={{ type: 'device', device, name: device?.DeviceName || uno }}
        onClose={() => setUpdateOpen(false)}
      />

      {/* Keyed on the resolved location so the picker mounts once the id is
          known and loads the right site's saved networks. */}
      <WifiPickerDialog
        key={`wifi-${wifiLocationId}`}
        open={wifiOpen}
        locationId={wifiLocationId === undefined ? null : wifiLocationId}
        deviceName={title}
        submitLabel="Add Wi-Fi"
        onClose={() => setWifiOpen(false)}
        onSubmit={sendAddWifi}
      />

      <WifiRemoveDialog open={removeOpen} uno={uno} onClose={() => setRemoveOpen(false)} />
    </>
  );
}
